import { Injectable, OnDestroy } from '@angular/core'
import { Subscription } from 'rxjs'
import { Apollo } from 'apollo-angular'
import gql from 'graphql-tag'
import {
  UpdateKeywordInput,
  CreateKeywordInput,
  DeleteKeywordInput,
  CreateKeywordsInput,
  DeclineRemainingKeywordSubmissionsInput,
} from '../inputs/keyword.inputs'
import { RemoveDocumentInput, CreateDocumentInput } from '../inputs/document.inputs'
import {
  KeywordsPageQuery,
  fragments,
  EditKeywordPageQuery,
  EditKeywordPageData,
} from '../queries/keyword.queries'
import { graphqlPatch, graphqlDelete } from '../mutation-helper'
import { errorToast, successToast } from '../../toaster'
import get from 'lodash-es/get'
import random from 'lodash-es/random'
import { KeywordActions } from '../../actions/keyword-actions'
import { parsedUrl } from '../../utils'
import { map } from 'rxjs/operators'
import { Router } from '@angular/router'
import { sweetAlert } from 'bmAlert'

const createKeyword = gql`
  mutation createKeywordMutation($input: CreateCustomerKeywordMutationInput!) {
    CreateCustomerKeywordMutation(input: $input) {
      errors
      keyword {
        ...KeywordsPageKeyword
        customer {
          ...KeywordsPageCustomer
        }
      }
    }
  }
  ${fragments.keyword}
  ${fragments.customer}
`

const createMultipassKeyword = gql`
  mutation CreateMultipassKeywordMutation($input: CreateMultipassKeywordMutationInput!) {
    CreateMultipassKeywordMutation(input: $input) {
      errors
      keyword {
        ...KeywordsPageKeyword
        customer {
          ...KeywordsPageCustomer
        }
      }
    }
  }
  ${fragments.keyword}
  ${fragments.customer}
`

const createKeywords = gql`
  mutation createKeywordsMutation($input: CreateCustomerKeywordsMutationInput!) {
    CreateCustomerKeywordsMutation(input: $input) {
      errors
      keywords {
        id
        phrase
        customer {
          id
          businessName
        }
      }
    }
  }
`

const updateKeyword = gql`
  mutation updateKeywordMutation($input: UpdateCustomerKeywordMutationInput!) {
    UpdateCustomerKeywordMutation(input: $input) {
      errors
      keyword {
        ...KeywordsPageKeyword
        customer {
          ...KeywordsPageCustomer
        }
      }
    }
  }
  ${fragments.keyword}
  ${fragments.customer}
`

const updateMultipassKeyword = gql`
  mutation UpdateMultipassKeywordMutation($input: UpdateMultipassKeywordMutationInput!) {
    UpdateMultipassKeywordMutation(input: $input) {
      errors
      keyword {
        ...KeywordsPageKeyword
        customer {
          ...KeywordsPageCustomer
        }
      }
    }
  }
  ${fragments.keyword}
  ${fragments.customer}
`

const createDocument = gql`
  mutation createDocumentMutation($input: CreateKeywordDocumentMutationInput!) {
    CreateKeywordDocumentMutation(input: $input) {
      errors
      document {
        id
        name
        fileUrl
        path
        keyword {
          id
        }
      }
    }
  }
`

const removeDocument = gql`
  mutation removeDocumentMutation($input: RemoveKeywordDocumentMutationInput!) {
    RemoveKeywordDocumentMutation(input: $input) {
      errors
      document {
        id
        name
        keyword {
          id
        }
      }
    }
  }
`

const toggleKeywordExpanded = gql`
  mutation ($id: Int, $expanded: Boolean) {
    ToggleKeywordExpandedMutation(id: $id, expanded: $expanded) @client
  }
`

const toggleKeywordEditing = gql`
  mutation ($id: Int, $editing: Boolean) {
    ToggleKeywordEditingMutation(id: $id, editing: $editing) @client
  }
`

const declineKeywordSubmissions = gql`
  mutation DeclineKeywordSubmissionsMutation($input: DeclineKeywordSubmissionsMutationInput!) {
    DeclineKeywordSubmissionsMutation(input: $input) {
      errors
    }
  }
`

const deleteKeyword = gql`
  mutation ($input: DeleteCustomerKeywordMutationInput!) {
    DeleteCustomerKeywordMutation(input: $input) {
      errors
      keyword {
        id
        state
        customer {
          ...KeywordsPageCustomer
        }
      }
    }
  }
  ${fragments.customer}
`

@Injectable({ providedIn: 'root' })
export class KeywordMutations implements OnDestroy {
  private mutateSub: Subscription
  private paymentFailedVerboseMessage: string = `<div class="m-3 small text-muted">Don't worry, we'll save your progress so you can complete this request when ready.</div>`

  constructor(
    private apollo: Apollo,
    private keywordActions: KeywordActions,
    private router: Router
  ) {}

  private createDocument({ keyword, file, url }): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const u = parsedUrl(url)
      const forServer = {
        fileUrl: u.protocol + '//' + u.hostname + u.pathname,
        path: u.pathname,
        name: file.name,
      }

      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: createDocument,
          variables: {
            input: {
              customerId: keyword.customerId,
              keywordId: keyword.id,
              document: forServer,
            },
          },
        })
        .subscribe(resolve)
    })
  }

  private duplicateDocument({ keyword, document }): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: createDocument,
          variables: {
            input: {
              customerId: keyword.customerId,
              keywordId: keyword.id,
              document: {
                fileUrl: document.fileUrl,
                path: document.path,
                name: document.name,
              },
            },
          },
        })
        .subscribe(resolve)
    })
  }

  private isPaymentError(errorMessage: string): boolean {
    return errorMessage.startsWith('Purchase failed')
  }

  private navigateToPaymentPage({ customerId, keywordId }): void {
    successToast('Your request has been saved as a draft')
    const returnUrl = `/dashboard/${customerId}/topics/${keywordId}/edit`
    // CanDeactivateKeywordPage ignores this route
    this.router.navigate(['/dashboard', 'billing_info', 'edit'], {
      queryParams: { editing: true, returnUrl },
    })
  }

  createKeyword(
    { customerId, body, documents, files }: CreateKeywordInput,
    isMultipassCustomer: boolean
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      const createKeywordMutation = isMultipassCustomer ? createMultipassKeyword : createKeyword
      this.mutateSub = this.apollo
        .mutate({
          mutation: createKeywordMutation,
          variables: {
            input: {
              customerId: customerId,
              body: body,
            },
          },
          refetchQueries: [
            {
              query: KeywordsPageQuery,
              variables: { customerId },
            },
          ],
        })
        .pipe(
          map(({ data }) =>
            data['CreateMultipassKeywordMutation']
              ? data['CreateMultipassKeywordMutation']
              : data['CreateCustomerKeywordMutation']
          )
        )
        .subscribe(
          (data) => {
            const { keyword, errors } = data
            if (errors?.length) {
              const errorMessage = data.errors[0]
              if (this.isPaymentError(errorMessage)) {
                sweetAlert
                  .openPaymentFailedModal(this.paymentFailedVerboseMessage)
                  .then(({ value: clickedUpdatePaymentButton, dismiss }) => {
                    if (clickedUpdatePaymentButton) {
                      this.createKeyword(
                        { customerId, body: { ...body, state: 'paused' }, documents, files },
                        isMultipassCustomer
                      ).then(({ keyword }) => {
                        this.navigateToPaymentPage({ customerId, keywordId: keyword.id })
                      })
                    }
                  })
              } else {
                errors.forEach((e) => errorToast(e))
              }
              reject()
            } else if (keyword) {
              // upload files
              if (documents?.length > 0) {
                documents.map((doc, i) =>
                  this.duplicateDocument({ keyword, document: documents[i] })
                )
              }
              if (files.length > 0) {
                const filePromises = files.map((f) => this.keywordActions.uploadFile(f))
                Promise.all(filePromises).then((urls) => {
                  urls.map((url, i) => this.createDocument({ keyword, file: files[i], url }))
                })
              }
              if (data) {
                resolve(data)
              }
            }
          },
          (error) => {
            errorToast(error)
            reject()
          }
        )
    })
  }

  createKeywords({ keywords }: CreateKeywordsInput): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: createKeywords,
          variables: {
            input: {
              keywords,
            },
          },
        })
        .subscribe(
          ({ data }: { data: any }) => {
            resolve(data.CreateCustomerKeywordsMutation)
          },
          (error) => {
            errorToast(error)
          }
        )
    })
  }

  updateKeyword(
    { customerId, id, updates }: UpdateKeywordInput,
    isMultipassCustomer: boolean
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()

      const updateKeywordMutation = isMultipassCustomer ? updateMultipassKeyword : updateKeyword
      this.mutateSub = this.apollo
        .mutate({
          mutation: updateKeywordMutation,
          variables: {
            input: {
              customerId,
              id,
              patch: graphqlPatch(updates),
              delete: graphqlDelete(updates),
            },
          },
        })
        .pipe(
          map(({ data }) => {
            if (isMultipassCustomer) {
              return data['UpdateMultipassKeywordMutation']
            } else {
              return data['UpdateCustomerKeywordMutation']
            }
          })
        )
        .subscribe(
          (data) => {
            if (data?.errors?.length) {
              const errorMessage = data.errors[0]
              if (this.isPaymentError(errorMessage)) {
                sweetAlert
                  .openPaymentFailedModal(this.paymentFailedVerboseMessage)
                  .then(({ value: clickedUpdatePaymentButton, dismiss }) => {
                    if (clickedUpdatePaymentButton) {
                      this.updateKeyword(
                        { customerId, id, updates: { ...updates, state: 'paused' } },
                        isMultipassCustomer
                      ).then(({ keyword }) => {
                        this.navigateToPaymentPage({ customerId, keywordId: keyword.id })
                      })
                    }
                  })
              } else {
                data.errors.forEach((e) => errorToast(e))
              }
              reject()
            } else {
              if (data) {
                this.toggleKeywordEditing({ id, editing: false })
                resolve(data)
              }
            }
          },
          (error) => {
            errorToast(error)
            reject()
          }
        )
    })
  }

  deleteKeyword({ customerId, id }: DeleteKeywordInput): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: deleteKeyword,
          variables: {
            input: { customerId, id },
          },
          refetchQueries: [
            {
              query: KeywordsPageQuery,
              variables: { customerId },
            },
          ],
        })
        .subscribe(
          ({ data }: { data: any }) => {
            if (get(data, 'DeleteCustomerKeywordMutation.errors', []).length > 0) {
              data.DeleteCustomerKeywordMutation.errors.forEach((e) => errorToast(e))
            } else {
              if (data) {
                this.toggleKeywordEditing({ id, editing: false })
                this.toggleKeywordExpanded({ id, expanded: false })
                resolve(data.DeleteCustomerKeywordMutation)
              }
            }
          },
          (error) => {
            errorToast(error)
          }
        )
    })
  }

  uploadKeywordDoc(
    { keyword, file }: CreateDocumentInput,
    multipass: boolean = false
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const docTempId = 'temp-' + random(0, 9999)
      const newDoc = {
        id: docTempId,
        __typename: 'Document',
        name: file.name,
        fileUrl: '',
        pending: true,
        path: '',
      }
      let data: EditKeywordPageData = {
        ...this.apollo.getClient().readQuery({
          query: EditKeywordPageQuery,
          variables: { customerId: keyword.customerId, keywordId: keyword.id, multipass },
        }),
      }
      const writableData = JSON.parse(JSON.stringify(data))
      const documents = data.customer.keyword.documents.concat([newDoc])
      Object.assign(writableData.customer.keyword, keyword, { documents })
      this.apollo.getClient().writeQuery({
        query: EditKeywordPageQuery,
        data: writableData,
        variables: { customerId: keyword.customerId, keywordId: keyword.id, multipass },
      })
      this.keywordActions.uploadFile(file).then((url) => {
        this.createDocument({ keyword, file, url }).then(
          (syncedData) => {
            if (data && syncedData.errors) {
              syncedData.errors.forEach((e) => errorToast(e))
            } else if (data) {
              const doc = writableData.customer.keyword.documents.find(
                (d: { id: string }) => d.id === newDoc.id
              )
              const savedDoc = syncedData.data.CreateKeywordDocumentMutation.document
              if (doc) {
                doc.id = savedDoc.id
                doc.fileUrl = savedDoc.fileUrl
                doc.path = savedDoc.path
                doc.pending = false
              }
              this.apollo.getClient().writeQuery({
                query: EditKeywordPageQuery,
                data: writableData,
                variables: { customerId: keyword.customerId, keywordId: keyword.id, multipass },
              })
              resolve(writableData)
            }
          },
          (error) => errorToast(error)
        )
      })
    })
  }

  removeKeywordDoc(
    { customerId, keyword, id }: RemoveDocumentInput,
    multipass: boolean = false
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      // Optimistic doc removal
      const data: EditKeywordPageData = this.apollo.getClient().readQuery({
        query: EditKeywordPageQuery,
        variables: { customerId, keywordId: keyword.id, multipass },
      })
      const writableData = JSON.parse(JSON.stringify(data))
      const documents = writableData.customer.keyword.documents.filter((d) => d.id !== id)
      Object.assign(writableData.customer.keyword, keyword, { documents })
      this.apollo.getClient().writeQuery({
        query: EditKeywordPageQuery,
        data: writableData,
        variables: { customerId, keywordId: keyword.id, multipass },
      })
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: removeDocument,
          variables: {
            input: {
              id,
              customerId,
              keywordId: keyword.id,
            },
          },
        })
        .subscribe(
          ({ data }: { data: any }) => {
            if (data && data.errors) {
              data.errors.forEach((e) => errorToast(e))
            } else {
              resolve(data)
            }
          },
          (error) => {
            errorToast(error)
          }
        )
    })
  }

  toggleKeywordExpanded({ id, expanded }: { id: number; expanded: boolean }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: toggleKeywordExpanded,
          variables: { id, expanded },
        })
        .subscribe(() => resolve())
    })
  }

  toggleKeywordEditing({ id, editing }: { id: number; editing: boolean }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: toggleKeywordEditing,
          variables: { id, editing },
        })
        .subscribe(() => resolve())
    })
  }

  declineRemainingKeywordSubmissions({
    id,
  }: DeclineRemainingKeywordSubmissionsInput): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.mutateSub) this.mutateSub.unsubscribe()
      this.mutateSub = this.apollo
        .mutate({
          mutation: declineKeywordSubmissions,
          variables: {
            input: {
              id,
            },
          },
        })
        .pipe(
          map(({ data }) => {
            return data['DeclineKeywordSubmissionsMutation']
          })
        )
        .subscribe(
          (data) => {
            if (data?.errors?.length) {
              data.errors.forEach((e) => errorToast(e))
              reject()
            } else {
              if (data) {
                resolve(data)
              }
            }
          },
          (error) => {
            console.log('error', error)
            errorToast(error)
            reject()
          }
        )
    })
  }

  ngOnDestroy(): void {
    if (this.mutateSub) this.mutateSub.unsubscribe()
  }
}
